/** Sidebar / module key for the Leads area (used by CRM guards only). */
export const ROUTE_KEY_LEADS = "leads" as const;

/* ── Control value ranking for merge precedence ── */

function rankControl(v: any): number {
  if (v === "admin") return 6;
  if (v === "manage" || v === "allow") return 5;
  if (v === "edit") return 4;
  if (v === "view" || v === "own") return 3;
  if (v === "deny") return 1;
  return 0;
}

function mergeControlValue(a: any, b: any): string {
  if (rankControl(b) >= rankControl(a)) return b ?? a ?? "deny";
  return a ?? "deny";
}

/**
 * NEW per-stage lead-feature shape:
 *   { level: "admin" | "manage" | "edit" | "view" | "deny" }
 * (See docs/rbac-redesign-backend-changes.md §10.) Anything else is treated
 * as the legacy action-map shape and routed through the merge into
 * `global.lead_management` so old policies keep working.
 */
function isNewLeadFeatureShape(value: unknown): boolean {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  return typeof (value as Record<string, unknown>).level === "string";
}

/** Per-stage feature keys owned by the redesigned wizard (doc §10). */
const NEW_STAGE_LEAD_FEATURE_KEYS = [
  "lead_management",
  "lead_deadline",
  "lead_followup",
  "lead_activity",
  "lead_bulk_edit",
] as const;

/**
 * Per-stage keys that USED to exist in the wizard but were dropped. We strip
 * them on hydration so re-saving an old role removes the dead data from the
 * DB row instead of silently round-tripping it forever.
 *
 *   - `lead_documents`: replaced by the dedicated `document_management`
 *     section that already lives on every stage.
 */
const DEAD_STAGE_FEATURES = ["lead_documents"] as const;

function stagesUseNewLeadShape(stages: unknown[]): boolean {
  if (!Array.isArray(stages)) return false;
  for (const stage of stages) {
    const perms = (stage as { permissions?: Record<string, unknown> })
      ?.permissions;
    if (!perms) continue;
    for (const key of NEW_STAGE_LEAD_FEATURE_KEYS) {
      if (isNewLeadFeatureShape(perms[key])) return true;
    }
  }
  return false;
}

/**
 * Normalizes raw `access_config` from the API for client use.
 *
 * - When the row uses the NEW per-stage shape (any stage has a
 *   `{ level: ... }` lead feature), drop the legacy `global.lead_management`
 *   action map entirely so the wizard renders a clean state. Stages are
 *   passed through verbatim — they are authoritative.
 * - For purely-legacy rows, keep the historical merge: collapse stage
 *   `lead_management` into `global.lead_management` and strip from stages
 *   so the existing read-side consumers keep working.
 */
export function normalizeAccessConfigForClient(raw: any): any {
  if (!raw || typeof raw !== "object") return raw ?? null;

  const global = { ...(raw.global ?? {}) };
  const stages = Array.isArray(raw.stages) ? raw.stages : [];
  const usesNewShape = stagesUseNewLeadShape(stages);

  if (usesNewShape) {
    // Redesigned wizard owns lead permissions per-stage — drop the legacy
    // global block (it's stale data, kept around only by old saves) so the
    // hydrated form mirrors exactly what's authoritative.
    delete global.lead_management;
  } else {
    // Build merged global.lead_management for legacy rows.
    let lm: Record<string, string> = {};

    const existing = global.lead_management;
    if (existing && typeof existing === "object" && !Array.isArray(existing)) {
      for (const [k, v] of Object.entries(existing)) {
        if (typeof v === "string") lm[k] = v;
      }
    }
    const keysFromGlobal = new Set(Object.keys(lm));

    for (const stage of stages) {
      const slm = stage?.permissions?.lead_management;
      if (!slm || typeof slm !== "object" || Array.isArray(slm)) continue;
      if (isNewLeadFeatureShape(slm)) continue;
      for (const [key, val] of Object.entries(slm as Record<string, any>)) {
        if (typeof val !== "string") continue;
        if (keysFromGlobal.has(key)) continue;
        lm[key] = mergeControlValue(lm[key], val);
      }
    }

    // Assignment is configured under global.assignation_management.
    for (const dep of ["assign", "bulk_assign", "builk_assign", "reassign"] as const) {
      delete lm[dep];
    }

    if (Object.keys(lm).length > 0) {
      global.lead_management = lm;
    }
  }

  // Migrate legacy per-field custom field value access: { access } → { view, edit }
  for (const [key, mod] of Object.entries(global)) {
    if (
      !key.startsWith("custom_field_") ||
      !mod ||
      typeof mod !== "object" ||
      Array.isArray(mod)
    )
      continue;
    const m = mod as Record<string, unknown>;
    if (m.view !== undefined || m.edit !== undefined) continue;
    if (m.access === undefined) continue;
    const v = m.access === "allow" ? "allow" : "deny";
    const next = { ...m, view: v, edit: v } as Record<string, unknown>;
    delete next.access;
    (global as Record<string, unknown>)[key] = next;
  }

  // Coerce stageId / sourceId to numbers so RBAC lookups match pipeline ids
  // (JSON may use strings). For LEGACY rows we additionally strip stage
  // `lead_management` because it was already merged into global above; for
  // NEW-shape rows we keep stage permissions verbatim — they're authoritative.
  const strippedStages = stages.map((stage: any) => {
    const perms = stage?.permissions;
    let next = { ...stage };
    if (next.stageId != null && next.stageId !== "") {
      const n = Number(next.stageId);
      if (Number.isFinite(n)) next = { ...next, stageId: n };
    }
    if (!perms || typeof perms !== "object") return next;

    // Always drop dead per-stage feature keys (e.g. lead_documents) so old
    // DB rows clean up on the next save through the wizard.
    let cleaned: Record<string, unknown> = perms as Record<string, unknown>;
    let mutated = false;
    for (const dead of DEAD_STAGE_FEATURES) {
      if (dead in cleaned) {
        if (!mutated) {
          cleaned = { ...cleaned };
          mutated = true;
        }
        delete cleaned[dead];
      }
    }

    if (usesNewShape) {
      return mutated ? { ...next, permissions: cleaned } : next;
    }
    if (isNewLeadFeatureShape((cleaned as any).lead_management)) {
      return mutated ? { ...next, permissions: cleaned } : next;
    }
    const { lead_management: _, ...rest } = cleaned;
    return { ...next, permissions: rest };
  });

  const normalizedSources = Array.isArray(raw.sources)
    ? raw.sources.map((src: any) => {
        if (src?.sourceId == null || src.sourceId === "") return src;
        const n = Number(src.sourceId);
        return Number.isFinite(n) ? { ...src, sourceId: n } : src;
      })
    : [];

  return {
    ...raw,
    global,
    stages: strippedStages,
    sources: normalizedSources,
    routeKeys: Array.isArray(raw.routeKeys) ? raw.routeKeys : [],
  };
}
